import { defineComponent as _defineComponent } from 'vue'
import { latex_units, roundToTwoDigits } from '@/services/unit.service'
import { exportMaterialList } from '@/modules/model/model.service'
import { modelViewerStore } from '@/store/modelViewer.store'
import { modelStore } from '@/modules/model/model.store'
import { StructuralMember } from '@consteel/storm'
import { computed, ref } from 'vue'
import { CsFlex } from '@consteel/csuetify'
import ModelInformationCard from './ModelInformationCard.vue'
import ChevronDownSvg from '@/assets/common/ChevronDown.svg.vue'
import { Model } from '@consteel/smadsteelloader'
import worker from 'simple-web-worker'
import { computedAsync } from '@vueuse/core'
import { getStructuralMember, getStructuralMembers } from '@/modules/storm'

// DATA + HOOKS


export default /*#__PURE__*/_defineComponent({
  __name: 'ModelInformation',
  setup(__props) {

const totalLengthLoading = ref(false)
const totalLengthSecondaryLoading = ref(false)
const totalSurfaceLoading = ref(false)
const totalSurfaceSecondaryLoading = ref(false)
const totalWeightLoading = ref(false)
const totalWeightSecondaryLoading = ref(false)
const open = ref(true)

// FUNCTIONS

const getNumberOfBars = (structuralMembers: StructuralMember[]): number => {
  return structuralMembers.length
}

const getTotalLength = async (structuralMembers: StructuralMember[]): Promise<string> => {
  const length = await worker.run(
    (members: StructuralMember[]) => {
      return members.reduce<number>(
        (sum: number, item: StructuralMember) => sum + (item.geometry?.length || 0),
        0
      )
    },
    [structuralMembers]
  )

  return roundToTwoDigits(length)
}

const getTotalSurface = async (structuralMembers: StructuralMember[]): Promise<string> => {
  const surface = await worker.run(
    (members: StructuralMember[]) => {
      return members.reduce<number>((sum: number, item: StructuralMember) => {
        const perimeter = item.section?.properties?.perimeter || 0

        return sum + perimeter * (item.geometry?.length || 0)
      }, 0)
    },
    [structuralMembers]
  )

  return roundToTwoDigits(surface)
}

const getTotalWeight = async (structuralMembers: StructuralMember[]): Promise<string> => {
  const weight = await worker.run(
    (members: StructuralMember[]) => {
      return (
        members.reduce<number>((sum, item) => {
          const area = item.section?.properties?.area
          const material = members.find((member) => member.id === item.id)?.material

          if (!material) {
            console.error('No material found')
            return sum
          }

          if (material.typeName === 'CONCRETE') return sum

          if (area && material.density) {
            return sum + area * (item.geometry?.length ?? 0) * material.density
          }
          return sum
        }, 0) / 1000
      )
    },
    [structuralMembers]
  )

  return roundToTwoDigits(weight)
}

const getStructuralMembersFromModel = (model: Model): StructuralMember[] => {
  let structuralMembers: StructuralMember[] = []

  if (selectedIds.value.length) {
    selectedIds.value.forEach((id) => {
      const item = getStructuralMember(model, id)

      if (item) structuralMembers.push(item)
    })
  } else structuralMembers = getStructuralMembers(model)

  return structuralMembers
}

const handleExportMaterialList = () => {
  exportMaterialList(modelStore.model)
}

// COMPUTED

const workerLoading = computed(
  (): boolean =>
    totalLengthLoading.value ||
    totalLengthSecondaryLoading.value ||
    totalSurfaceLoading.value ||
    totalSurfaceSecondaryLoading.value ||
    totalWeightLoading.value ||
    totalWeightSecondaryLoading.value
)

const compare = computed((): boolean => {
  return !!(model.value && secondaryModel.value)
})

const numberOfBarsChanged = computed((): boolean => {
  return numberOfBars.value !== numberOfBarsSecondary.value
})

const totalWeightChanged = computed((): boolean => {
  return totalWeight.value !== totalWeightSecondary.value
})

const totalSurfaceChanged = computed((): boolean => {
  return totalSurface.value !== totalSurfaceSecondary.value
})

const totalLengthChanged = computed((): boolean => {
  return totalLength.value !== totalLengthSecondary.value
})

const primaryModelNumber = computed((): string | null | undefined => {
  return modelViewerStore.model.modelNumber
})

const secondaryModelNumber = computed((): string | null | undefined => {
  return modelViewerStore.secondaryModel.modelNumber
})

const selectedIds = computed((): string[] => {
  return modelViewerStore.selectedIds
})

const model = computed((): Model | null | undefined => {
  return modelViewerStore.model.rawSmadsteelModel
})

const secondaryModel = computed((): Model | null | undefined => {
  return modelViewerStore.secondaryModel.rawSmadsteelModel
})

const structuralMembers = computed((): StructuralMember[] => {
  if (!model.value) return []

  return getStructuralMembersFromModel(model.value)
})

const structuralMembersSecondary = computed((): StructuralMember[] => {
  if (!secondaryModel.value) return []

  return getStructuralMembersFromModel(secondaryModel.value)
})

const numberOfBars = computed((): number => {
  return getNumberOfBars(structuralMembers.value)
})

const numberOfBarsSecondary = computed((): number => {
  return getNumberOfBars(structuralMembersSecondary.value)
})

const lengthUnit = computed((): string => {
  return latex_units.m
})

const surfaceUnit = computed((): string => {
  return latex_units.m2
})

const weightUnit = computed((): string => {
  return latex_units.t
})

const totalLength = computedAsync<string>(
  async (): Promise<string> => {
    totalLengthLoading.value = true
    const totalLength = await getTotalLength(structuralMembers.value)
    totalLengthLoading.value = false

    return totalLength
  },
  '',
  totalLengthLoading
)

const totalLengthSecondary = computedAsync<string>(
  async (): Promise<string> => {
    totalLengthSecondaryLoading.value = true
    const totalLengthSecondary = await getTotalLength(structuralMembersSecondary.value)
    totalLengthSecondaryLoading.value = false

    return totalLengthSecondary
  },
  '',
  totalLengthSecondaryLoading
)

const totalSurface = computedAsync(
  async (): Promise<string> => {
    totalSurfaceLoading.value = true
    const totalSurface = await getTotalSurface(structuralMembers.value)
    totalSurfaceLoading.value = false

    return totalSurface
  },
  '',
  totalSurfaceLoading
)

const totalSurfaceSecondary = computedAsync(
  async (): Promise<string> => {
    totalSurfaceSecondaryLoading.value = true
    const totalSurfaceSecondary = await getTotalSurface(structuralMembersSecondary.value)
    totalSurfaceSecondaryLoading.value = false

    return totalSurfaceSecondary
  },
  '',
  totalSurfaceSecondaryLoading
)

const totalWeight = computedAsync<string>(
  async (): Promise<string> => {
    totalWeightLoading.value = true
    const totalWeight = await getTotalWeight(structuralMembers.value)
    totalWeightLoading.value = false

    return totalWeight
  },
  '',
  totalWeightLoading
)

const totalWeightSecondary = computedAsync(
  async (): Promise<string> => {
    totalWeightSecondaryLoading.value = true
    const totalWeightSecondary = await getTotalWeight(structuralMembersSecondary.value)
    totalWeightSecondaryLoading.value = false

    return totalWeightSecondary
  },
  '',
  totalWeightSecondaryLoading
)

return { __sfc: true,totalLengthLoading, totalLengthSecondaryLoading, totalSurfaceLoading, totalSurfaceSecondaryLoading, totalWeightLoading, totalWeightSecondaryLoading, open, getNumberOfBars, getTotalLength, getTotalSurface, getTotalWeight, getStructuralMembersFromModel, handleExportMaterialList, workerLoading, compare, numberOfBarsChanged, totalWeightChanged, totalSurfaceChanged, totalLengthChanged, primaryModelNumber, secondaryModelNumber, selectedIds, model, secondaryModel, structuralMembers, structuralMembersSecondary, numberOfBars, numberOfBarsSecondary, lengthUnit, surfaceUnit, weightUnit, totalLength, totalLengthSecondary, totalSurface, totalSurfaceSecondary, totalWeight, totalWeightSecondary, CsFlex, ModelInformationCard, ChevronDownSvg }
}

})